import { Arr } from '@deqo/foundation';
import { computeRotateCursor, computeScaleCursor } from '../../../../@resources/cursors/CursorHelpers';
import { Line } from '../../../../platform/geom/Line';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Polyline } from '../../../../platform/geom/Polyline';
import { Rect } from '../../../../platform/geom/Rect';
import { Camera } from '../../Camera';
import { Physical, RelativeTo } from '../../entities/Physical';
import { NetPart } from '../../nets/Net';
import { SelectionRotateTransformation } from './SelectionRotateTransformation';
import { SelectionScaleTransformation } from './SelectionScaleTransformation';
import { SelectionSwapTransformation } from './SelectionSwapTransformation';
import { SelectionTweakTransformation } from './SelectionTweakTransformation';

const frame = (() => {

    const base = Rect.fromEdges(-.5, -.5, .5, .5);
    const center = base.center();
    const northEast = base.topLeft();
    const north = new Point(center.x, base.top);
    const northWest = base.topRight();
    const west = new Point(base.right, center.y);
    const southWest = base.bottomRight();
    const south = new Point(center.x, base.bottom);
    const southEast = base.bottomLeft();
    const east = new Point(base.left, center.y);

    const list = [northEast, north, northWest, west, southWest, south, southEast, east];
    const map = {
        northEast,
        north,
        northWest,
        west,
        southWest,
        south,
        southEast,
        east,
    };

    return {
        base,
        center,
        bearings: map,
        opp(b: Point) {
            return Arr.at(list, list.indexOf(b) + 4);
        },
        adj(b: Point) {
            return [
                Arr.at(list, list.indexOf(b) - 1),
                Arr.at(list, list.indexOf(b) + 1),
            ] as const;
        },
    };

})();

export type TransformNetBearing = keyof typeof frame.bearings;

export const TransformHandle = {

    rotate(bearing: string, camera: Camera, rmm: Matrix): NetPart {

        const rHandle = frame.bearings[bearing];
        const rOrigin = frame.center;

        const handle = rmm.apply(rHandle);
        const origin = rmm.apply(rOrigin);
        const axis = rHandle.subtract(rOrigin).normalize();

        const [_, rotation] = rmm.decompose();

        const data = {
            cursor: computeRotateCursor(rotation + axis.angle()),
            impl: SelectionRotateTransformation,
            args: {
                origin,
                axis,
            },
        };

        return {
            id: bearing,
            type: 'transform',
            style: 'none',
            verticies: [handle],
            data,
            test(vpt) {

                const offset = axis.rotate(rotation).normalize().multiply(30 / camera.scale);
                const hitbox = new Polyline([
                    handle.add(offset),
                    handle.add(offset.perp()),
                    handle.add(offset.perpcw()),
                    handle.add(offset),
                ]);

                // DebugOverlay.polyline('RH' + bearing, hitbox.points, 'red', true);
                return camera.toView(hitbox).contains(vpt);

            }
        };

        // return new TransformHandle(name, 'rotate', 'none', handle, [handle], origin, axis,
        //     (vpt, modelView) => {
        //         const vp = modelView.apply(handle);
        //         const ab = new Line(vp, vp.add(normal.multiply(25)));
        //         const cd = ab.rotate(ab.mid, toRadians(90));
        //         const abcd = Polyline.fromHull([ab.p1, cd.p1, ab.p2, cd.p2]);
        //         return abcd.contains(vpt);
        //     });
    },

    scaleCorner(bearing: TransformNetBearing, camera: Camera, rmm: Matrix): NetPart {

        const rHandle = frame.bearings[bearing];
        const rOrigin = frame.opp(rHandle);

        const handle = rmm.apply(rHandle);
        const origin = rmm.apply(rOrigin);
        const axis = rHandle.subtract(rOrigin).normalize();

        const data = {
            cursor: computeScaleCursor(rmm.decompose2().rotate + axis.angle()),
            impl: SelectionScaleTransformation,
            args: {
                origin,
                axis,
                handle,
            },
        };

        return {
            id: bearing,
            type: 'transform',
            style: 'node',
            verticies: [handle],
            data,
            test(vpt) {
                const rvm = camera.modelViewMatrix.multiply(rmm);
                const { scale, rotate } = rvm.decompose2();
                const compact = Math.min(scale.x, scale.y) < 30;

                const size = (compact ? 10 : 20) / camera.scale;
                const offset = size * Math.sqrt(2);
                const hitbox = Matrix.identity
                    .translate(handle)
                    .translate(compact ? axis.multiply(offset / 2) : Point.zero)
                    .rotate(rotate)
                    .scale(size)
                    .apply(frame.base);

                // DebugOverlay.polyline('SCH' + bearing, hitbox, 'green', true);
                return Polyline.from(camera.toView(hitbox), true).contains(vpt);
            }
        };
    },

    scaleEdge(bearing: TransformNetBearing, camera: Camera, rmm: Matrix): NetPart {

        const b = frame.bearings[bearing];
        const rOrigin = frame.opp(b);
        const [rp1, rp2] = frame.adj(b);

        const regularMin = 30;
        const hitDist = 8;

        const line = Line.from(rmm.apply([rp1, rp2]));
        const origin = rmm.apply(rOrigin);
        const axis = rp2.subtract(rp1).perp().normalize();

        const data = {
            cursor: computeScaleCursor(rmm.decompose2().rotate + axis.angle()),
            impl: SelectionScaleTransformation,
            args: {
                origin,
                axis,
                handle: line.mid,
                fixedMode: true,
            },
        };

        return {
            id: bearing,
            type: 'transform',
            style: 'node',
            verticies: line.toArray(),
            data,
            test(vpt) {
                const { scale } = camera.modelViewMatrix.multiply(rmm).decompose2();
                const compact = Math.min(scale.x, scale.y) < regularMin;
                const hitbox = camera.toView(line).extrude(hitDist, compact ? 0 : hitDist);

                // DebugOverlay.polyline('SEH' + bearing, camera.toModel(hitbox), 'orange', true);
                return Polyline.from(hitbox, true).contains(vpt);
            }
        };
    },

    swap(subject: Physical, camera: Camera, rmm: Matrix): NetPart {

        const handle = subject.center(RelativeTo.Model);

        const data = {
            impl: SelectionSwapTransformation,
            args: { subject },
        };

        return {
            id: `swap/${subject.id}`,
            type: 'transform',
            style: 'blob',
            verticies: [handle],
            data,
            test(vpt) {
                return vpt.distanceTo(camera.toView(handle)) < 10;
            }
        };
    },

    tweak(subject: Physical, camera: Camera, offset: number, style: 'node' | 'blob'): NetPart {

        const figure = subject.createFigure();
        const handle = figure[offset];

        const data = {
            cursor: 'grab',
            impl: SelectionTweakTransformation,
            args: { offset },
        };

        return {
            id: `tweak/${offset}`,
            type: 'transform',
            style: style || 'node',
            verticies: [handle],
            data,
            test(vpt) {
                return vpt.distanceTo(camera.toView(handle)) < 8;
            }
        };
    },

};


